import { OnboardingFlow } from "../features/onboarding/OnboardingFlow";
import { useEffect, useState } from "preact/hooks";
import { useLocation, useSearch } from "wouter-preact";
import { routes } from "../lib/route";
import { signInDestination } from "../features/auth/signInDestination";
import { selectPortalCredentials } from "../features/auth/authSelectors";
import { useAppSelector } from "../store/hooks";
import { useSiteConfig } from "../features/tenant/SiteConfigContext";
import { getApiFailureMessage } from "../lib/gboApiClient";
import {
  useStartPortalVerificationMutation,
  useResendPortalVerificationMutation,
  useAddPortalVerifiedEmailMutation,
  useLazyGetPortalUserQuery,
  useLogoutPortalMutation,
  useLazyGetPortalAccountsQuery,
  useSelectPortalAccountMutation,
  type VerificationChallenge,
} from "../store/portalAuthApi";

export function LoginTestPage() {
  const { tenantId, displayName } = useSiteConfig();
  return <LoginTest providerName={displayName} key={tenantId} />;
}

function LoginTest({ providerName }: { providerName: string }) {
  const [, navigate] = useLocation();
  const search = useSearch();
  const returnTo = signInDestination.fromSearch(search);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState<VerificationChallenge | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [start] = useStartPortalVerificationMutation();
  const [resend] = useResendPortalVerificationMutation();
  const [addVerifiedEmail] = useAddPortalVerifiedEmailMutation();
  const [logout] = useLogoutPortalMutation();
  const [selectAccount] = useSelectPortalAccountMutation();
  const [getUser, userQuery] = useLazyGetPortalUserQuery();
  const [getAccounts, accountsQuery] = useLazyGetPortalAccountsQuery();
  const credentials = useAppSelector(selectPortalCredentials);
  const user = credentials ? userQuery.currentData : undefined;
  const accounts = accountsQuery.currentData;

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (error) {
      setError(getApiFailureMessage(error, "Unable to complete this request."));
    } finally {
      setBusy(false);
    }
  }

  async function loadIdentity() {
    await getUser().unwrap();
    await getAccounts().unwrap();
  }

  useEffect(() => {
    if (!credentials) return;
    if (returnTo) navigate(returnTo, { replace: true });
    else void run(loadIdentity);
  }, [returnTo]);

  function completeSignIn() {
    if (returnTo) navigate(returnTo, { replace: true });
    else {
      navigate(routes.loginTest(), { replace: true });
      void run(loadIdentity);
    }
  }

  function verify(event: Event) {
    event.preventDefault();
    if (!challenge) return;
    void run(async () => {
      await addVerifiedEmail({ challengeId: challenge.challengeId, code }).unwrap();
      setChallenge(null);
      setCode("");
      setEmail("");
      await loadIdentity();
    });
  }

  return (
    <section class="search-page" aria-label="Portal login test">
      <header class="search-heading">
        <h2>Portal login test</h2>
        <p class="muted">Verify an email, select an account, and check the saved login.</p>
      </header>
      {error && (
        <p class="payment-error" role="alert">
          {error}
        </p>
      )}
      <div class="search-options">
        <div class="search-card">
          <h3>{credentials ? "Signed in" : "Sign in"}</h3>
          {!credentials ? (
            <OnboardingFlow providerName={providerName} onComplete={completeSignIn} />
          ) : (
            <>
              {user && <h4>Verify another email</h4>}
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void run(async () => {
                    setChallenge(await start({ email }).unwrap());
                    setCode("");
                  });
                }}
              >
                <label class="form-control">
                  <span>Email address</span>
                  <input
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    disabled={busy}
                    onInput={(event) => {
                      setEmail(event.currentTarget.value);
                      setChallenge(null);
                    }}
                  />
                </label>
                <button class="primary-action" disabled={busy} type="submit">
                  Send verification code
                </button>
              </form>
              {challenge && (
                <form onSubmit={verify}>
                  <label class="form-control">
                    <span>Verification code</span>
                    <input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      required
                      value={code}
                      disabled={busy}
                      onInput={(event) => setCode(event.currentTarget.value)}
                    />
                  </label>
                  <p class="muted">
                    Code expires {new Date(challenge.expiresAt).toLocaleString()}. Resend available{" "}
                    {new Date(challenge.resendAvailableAt).toLocaleTimeString()}.
                  </p>
                  <button class="primary-action" disabled={busy} type="submit">
                    Verify and continue
                  </button>
                  <button
                    class="secondary-action"
                    disabled={busy}
                    type="button"
                    onClick={() =>
                      void run(async () => {
                        setChallenge(await resend({ challengeId: challenge.challengeId }).unwrap());
                        setCode("");
                      })
                    }
                  >
                    Resend code
                  </button>
                </form>
              )}
            </>
          )}
          {user && (
            <>
              <p>
                Verified emails: <strong>{user.verifiedEmails.join(", ")}</strong>
              </p>
              {accounts?.length === 0 && (
                <p role="status">
                  No accounts are linked to these emails. Contact your provider or verify another
                  email.
                </p>
              )}
              {accounts?.map((account) => (
                <button
                  key={account.customerId}
                  class="secondary-action"
                  type="button"
                  disabled={busy || user.customerId === account.customerId}
                  onClick={() =>
                    void run(async () => {
                      await selectAccount({ customerId: account.customerId }).unwrap();
                      await getUser().unwrap();
                    })
                  }
                >
                  {account.name} · {account.code}
                  {user.customerId === account.customerId ? " (selected)" : ""}
                </button>
              ))}
            </>
          )}
        </div>
        <div class="search-card">
          <h3>Session</h3>
          <p class="muted">
            Authenticated requests renew the JWT when it is within 30 seconds of expiration.
          </p>
          <p>
            Access expires:{" "}
            {credentials
              ? new Date(credentials.accessExpiresAt).toLocaleString()
              : "No saved login"}
          </p>
          <p>
            Refresh expires:{" "}
            {credentials ? new Date(credentials.refreshExpiresAt).toLocaleString() : "—"}
          </p>
          <button class="secondary-action" disabled={busy} onClick={() => void run(loadIdentity)}>
            Check current login
          </button>
          <button
            class="secondary-action"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const result = logout();
                userQuery.reset();
                accountsQuery.reset();
                setChallenge(null);
                setCode("");
                await result.unwrap();
              })
            }
          >
            Sign out
          </button>
          {user && (
            <dl>
              <dt>Tenant ID</dt>
              <dd style={{ overflowWrap: "anywhere", margin: 0 }}>{user.tenantId}</dd>
              <dt>Customer ID</dt>
              <dd style={{ overflowWrap: "anywhere", margin: 0 }}>
                {user.customerId ?? "Not selected"}
              </dd>
            </dl>
          )}
        </div>
      </div>
    </section>
  );
}
